import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/painel-iptv/pos-pago")({
  head: () => ({
    meta: [
      { title: "Painel IPTV Pós Pago - Créditos Sob Demanda | P2 PRO" },
      { name: "description", content: "O melhor Painel IPTV Pós Pago do Brasil. Tenha flexibilidade total com créditos sob demanda, suporte 24h e estabilidade 4K. Comece sua revenda profissional sem estoque fixo." },
      { name: "keywords", content: "painel iptv pos pago, revenda iptv pos pago, créditos iptv sob demanda, painel p2p pos pago" },
      { property: "og:title", content: "Painel IPTV Pós Pago - Liberdade para sua Revenda | P2 PRO" },
      { property: "og:description", content: "Descubra como funciona o sistema pós-pago da P2 PRO. Aumente seus lucros pagando apenas pelo que consumir." },
    ],
  }),
  component: () => (
    <SEOPage
      heroImage="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200"
      variant="centered"
      showTestimonials={true}
      hideBenefits={true}
      showPriceTable={true}
      category="Modalidade"
      sections={[
        {
          title: "Liberdade Financeira no Modelo Pós-Pago",
          type: "featured",
          content: (
            <div className="grid lg:grid-cols-2 gap-12 items-center text-white">
              <div>
                <h3 className="text-2xl font-black mb-6 italic uppercase">O fim dos créditos expirados</h3>
                <p className="text-white/80 leading-relaxed mb-8">
                  No modelo tradicional, revendedores perdem dinheiro com créditos que expiram ou ficam parados. Na nossa estrutura pós-pago, seu saldo é vitalício e você só paga quando ativa um cliente. É a eficiência máxima para o seu fluxo de caixa.
                </p>
                <div className="flex flex-col gap-4">
                  {[
                    "Sem taxa de adesão abusiva",
                    "Recargas mínimas acessíveis",
                    "Controle total via Dashboard",
                    "Relatórios financeiros detalhados"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white/5 p-4 rounded-xl border border-white/10">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                      <span className="font-bold text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="aspect-square rounded-[3rem] bg-gradient-to-br from-white/10 to-transparent border border-white/20 p-8 flex flex-col justify-center text-center">
                  <div className="text-6xl mb-6">📈</div>
                  <div className="text-4xl font-black mb-2 tracking-tighter italic">ROI ACELERADO</div>
                  <p className="text-white/60 text-sm">Nossos parceiros pós-pago relatam um aumento de até 40% na margem de lucro líquida no primeiro trimestre.</p>
                </div>
              </div>
            </div>
          )
        }
      ]}
      title="PAINEL IPTV PÓS PAGO: FLEXIBILIDADE TOTAL"
      subtitle="Aumente sua margem de lucro com um sistema de créditos sob demanda, ideal para quem busca liberdade financeira e gestão simplificada."
      introParagraphs={[
        "O Painel IPTV Pós Pago é a solução definitiva para revendedores que não desejam ficar presos a estoques fixos de créditos. Diferente do modelo tradicional, aqui você tem a liberdade de carregar seu painel de acordo com a sua demanda real, otimizando seu fluxo de caixa desde o primeiro dia.",
        "Na P2 PRO, nosso sistema pós-pago oferece a mesma infraestrutura de elite das nossas soluções corporativas. Isso significa que você terá acesso a servidores Tier 3, canais em 4K estáveis e um painel administrativo intuitivo para gerenciar seus clientes com total autonomia.",
        "Este modelo é perfeito tanto para quem está começando e quer testar o mercado com baixo investimento, quanto para revendedores experientes que precisam de agilidade na reposição de créditos para não deixar nenhum cliente na mão."
      ]}
      benefits={[
        {
          title: "Sem Estoque Parado",
          description: "Pague apenas pelo que seus clientes realmente consomem, evitando desperdício de capital.",
        },
        {
          title: "Créditos Instantâneos",
          description: "Sistema de recarga ágil que garante que seu cliente nunca fique sem acesso.",
        },
        {
          title: "Liberdade Financeira",
          description: "Gerencie seus ganhos e reinvestimentos de forma muito mais dinâmica e segura.",
        }
      ]}
      howItWorks={[
        { title: "Cadastro", description: "Você cria sua conta em nosso painel administrativo exclusivo para revenda." },
        { title: "Ativação", description: "Realiza a ativação do seu painel com um valor simbólico de segurança." },
        { title: "Gestão", description: "Adiciona clientes e gera testes gratuitos ilimitados para fechar vendas." },
        { title: "Consumo", description: "Consome créditos conforme vende, mantendo seu painel sempre ativo." }
      ]}
      diferenciais={[
        { title: "Estabilidade P2P/OTT", description: "Tecnologia anti-travamento de última geração para seus clientes." },
        { title: "Suporte VIP", description: "Acesso direto ao nosso time técnico para qualquer dúvida ou configuração." },
        { title: "Painel Multi-App", description: "Compatível com as principais TVs, TV Box e dispositivos móveis do mercado." }
      ]}
      faq={[
        {
          question: "Qual a diferença entre painel pré-pago e pós-pago?",
          answer: "No pré-pago você compra um lote fixo. No pós-pago da P2 PRO, você tem flexibilidade para adicionar créditos conforme sua necessidade cresce."
        },
        {
          question: "Existe valor mínimo de recarga?",
          answer: "Trabalhamos com valores acessíveis para garantir que sua margem de lucro seja a maior do mercado IPTV."
        },
        {
          question: "Os créditos expiram?",
          answer: "Não! Seus créditos ficam seguros no painel até que você decida ativá-los para um novo cliente."
        }
      ]}
      breadcrumbs={[
        { label: "Painel IPTV", href: "/painel-iptv" },
        { label: "Pós Pago", href: "/painel-iptv/pos-pago" }
      ]}
      relatedArticles={[
        { title: "Melhor Painel IPTV", href: "/painel-iptv/melhor" },
        { title: "Franquia IPTV", href: "/painel-iptv/franquia" },
        { title: "Painel para Revendedores", href: "/painel-iptv/revendedores" }
      ]}
    />
  ),
});
